import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { InterPoint, Logger } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Icon,
  Link as DSLink,
  ListRow,
  ListRowGroup,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodApplicationGroup,
  SodColumnChip,
  SodListLoader,
  SodMessage,
  SodReloader,
} from '../../../../shared/components';
import {
  AAG_SOD_ENTITLEMENT_REPORT,
  ABORT_ERROR,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_MED,
  ENTITLEMENT_DETAILS_TABS,
  SEARCH_TEXT_LENGTH,
  testIds,
  violationsEntitlement,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useInViewObserver,
  useNavigateTo,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type ListTrackerState } from '../../../../shared/types';
import { createDevRejectionHandler } from '../../../../shared/utils';
import { detailsActions } from '../../../Details';
import { entReportsActions } from '../../../EntitlementReports/data-store/entReportsSlice';
import {
  type EntitlementDetailsRowType,
  type EntitlementSidePanelGroupRowContextState,
  useGetEntitlementListMutation,
} from '../../data-layer';
import { entitlementActions, type EntKebabOptions } from '../../data-store';
import {
  configureTracker,
  getEntitlementListPayload,
  getRowContext,
} from '../../utils/utils-entitlementlist';

import './EntitlementTabList.css';

/**
 * EntitlementTabList Component
 *
 * Comprehensive list component that renders entitlements with SOD violations using infinite scroll.
 * Provides filtering, searching, sidepanel integration, and row interactions with configurable
 * columns, menu actions, and transition animations for optimal user experience.
 *
 * @component
 * @returns {React.ReactElement} List container with infinite scroll and interactions including:
 *   - Infinite scroll with observer-based loading
 *   - Configurable column display
 *   - Row selection and menu actions
 *   - Sidepanel integration
 *   - Loading states and error handling
 *
 * @example
 * <EntitlementTabList />
 */
interface TrackedEntitlementRowType extends EntitlementDetailsRowType {
  tracker: ListTrackerState;
}

const MAX_COUNT = 50;
const LEAVE_ANIM_DURATION = 600;

function EntitlementTabList(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const initialEntOptionsWithIcons: EntKebabOptions[] = useMemo(
    () => [
      {
        label: l('Generate Report'),
        value: 'report',
        icon: 'Report',
      },
      {
        label: l('View Details'),
        value: ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY,
        icon: 'ArrowRight',
      },
    ],
    [l]
  );

  const {
    entitlement: {
      controls: {
        activeFilters,
        activeColumns,
        searchKey,
        sortInAction,
        activeInsight,
      },
      list: { totalCount, rowIdToTransition, isRefresh },
      sidepanel: { isSidePanelVisible },
    },
    details: { activeTab: currentActiveTab },
    global: { isGlobalLoading },
  } = useTypedSelector((state) => state.aag);

  // API endpoints
  const [getEntitlementList, { isLoading: isListLoading }] =
    useGetEntitlementListMutation();
  const { navigateToDetails, navigateToUrl } = useNavigateTo();

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedEntitlementRowType[]);
  const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        totalCount === null ||
          (currentOffset < totalCount && listRows.length < totalCount)
      ),
    [currentOffset, totalCount, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && totalCount !== null && !isValidApiCall),
    [totalCount, isListZero, isValidApiCall]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  useEffect(() => {
    dispatch(entitlementActions.setIsListLoading(isListLoading));
  }, [dispatch, isListLoading]);

  // List API call
  const handleListApiCall = useCallback(
    async (offset: number) => {
      abortRef.current?.('New Search');

      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getEntitlementListPayload(
        activeFiltersMap,
        searchKey,
        sortInAction,
        activeInsight,
        { max: MAX_COUNT, offset }
      );

      try {
        const controller = getEntitlementList(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    [getEntitlementList, activeFilters, searchKey, sortInAction, activeInsight]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView || !isValidApiCall || isGlobalLoading) {
        return;
      }

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response as {
            totalRecords: number;
            data: EntitlementDetailsRowType[] | null | undefined;
          };

          if (!data) {
            dispatch(entitlementActions.setListTotalCount(0));

            return;
          }

          let newRows: TrackedEntitlementRowType[];

          try {
            newRows = configureTracker(data) as TrackedEntitlementRowType[];
          } catch (error) {
            Logger.error('Error in data transformation:', error, 'Data:', data);

            return;
          }

          setListRows((prev) => [
            ...prev.map((row) => ({
              ...row,
              tracker: {
                ...row.tracker,
                isFirstAddded: false,
              },
            })),
            ...newRows,
          ]);
          dispatch(entitlementActions.setListTotalCount(totalRecords));
        })
        .catch(
          createDevRejectionHandler('EntitlementTabList: handleListApiCall')
        );
    }, 10);

    return () => clearTimeout(debounceTimer);
  }, [
    dispatch,
    isGlobalLoading,
    isObserverInView,
    isValidApiCall,
    currentOffset,
    handleListApiCall,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        batch(() => {
          dispatch(entitlementActions.setListTotalCount(null));
          dispatch(entitlementActions.setIsSidePanelVisible(false));

          if (isRefresh) {
            dispatch(entitlementActions.setListIsRefresh(false));
          }
        });
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [
    // Add all the dependencies based on which you want to refresh the page
    // Removed activeInsight to prevent closing insights when insight is selected
    dispatch,
    isGlobalLoading,
    activeFilters,
    searchKey,
    sortInAction,
    isRefresh,
  ]);

  // Removing connectors if any when `list` mode and sidepanel closed
  useEffect(() => {
    if (!isSidePanelVisible) {
      setListRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        }))
      );
    }
  }, [isSidePanelVisible]);

  // Close sidepanel on component unmount or URL/tab changes
  useEffect(
    () => () => {
      if (isGlobalLoading && isSidePanelVisible) {
        dispatch(entitlementActions.setIsSidePanelVisible(false));
      }
    },
    [dispatch, isGlobalLoading, isSidePanelVisible]
  );

  // Clear search key and filters when becoming active tab
  useEffect(() => {
    if (!isGlobalLoading && currentActiveTab) {
      dispatch(entitlementActions.setSearchKey(null));
      dispatch(entitlementActions.setActiveFilters([]));
    }
  }, [dispatch, isGlobalLoading, currentActiveTab]);

  // Sidepanel handling and row connector lgic on `row` click
  const handleRowClick = useCallback(
    (id: string) => {
      let modifiedIndex: number | undefined;
      const rowContext: EntitlementSidePanelGroupRowContextState = {
        current: { id: '' },
      };
      const listLength = listRows.length;

      const modifiedRows = listRows.map((row, index) => {
        if (row.tracker.id === id) {
          modifiedIndex = index;

          return {
            ...row,
            tracker: {
              ...row.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      if (modifiedIndex !== undefined) {
        // Will always have the current row
        const { tracker: currentTracker, ...currentRowData } =
          modifiedRows[modifiedIndex];

        rowContext.current = getRowContext(
          currentRowData as EntitlementDetailsRowType
        );

        if (modifiedIndex < listLength - 1 && listLength > 1) {
          const { tracker: nextTracker, ...nextRowData } =
            modifiedRows[modifiedIndex + 1];

          rowContext.next = getRowContext(
            nextRowData as EntitlementDetailsRowType
          );
        }

        // First element is clicked
        if (modifiedIndex === 0 && listLength > 1) {
          const { tracker: firstNextTracker, ...firstNextRowData } =
            modifiedRows[modifiedIndex + 1];

          rowContext.next = getRowContext(
            firstNextRowData as EntitlementDetailsRowType
          );
        }

        setListRows(() => modifiedRows);

        dispatch(entitlementActions.setSidePanelRowContext(rowContext));

        if (!isSidePanelVisible) {
          dispatch(
            entitlementActions.setIsSidePanelVisible(!isSidePanelVisible)
          );
        }
      }
    },
    [dispatch, isSidePanelVisible, listRows]
  );

  // Transition logic here
  useEffect(() => {
    if (rowIdToTransition !== null) {
      const nextItem: { tracker?: ListTrackerState } = {};
      const listRowsLength = listRows.length;

      // Start the transition
      setListRows((prevListRows) =>
        prevListRows.map((row, index) => {
          if (row.tracker.id === rowIdToTransition) {
            // Check if next available item is present
            if (index !== listRowsLength - 1 && row.tracker.isRowDetailsOpen) {
              nextItem.tracker = listRows[index + 1].tracker;
            }

            return {
              ...row,
              tracker: { ...row.tracker, isTransition: true },
            };
          }

          return row;
        })
      );

      // End the transition and manage the next item with sidepanel
      setTimeout(() => {
        setListRows((prevListRows) =>
          prevListRows
            .filter((row) => row.tracker.id !== rowIdToTransition)
            .map((row) => {
              if (nextItem.tracker && row.tracker.id === nextItem.tracker.id) {
                return {
                  ...row,
                  tracker: { ...row.tracker, isRowDetailsOpen: true },
                };
              }

              return row;
            })
        );

        dispatch(entitlementActions.setListTransitionId(null));
      }, LEAVE_ANIM_DURATION);
    }
  }, [dispatch, rowIdToTransition, listRows]);

  const getCenterContent = useCallback(
    (
      item: TrackedEntitlementRowType,
      listConfigurableColumns: JSX.Element[]
    ) => {
      const {
        entitlementDisplayName,
        entitlementValue,
        endpointName,
        endPointLogo,
      } = item;

      return (
        <Box className='SodEntitlementListGroup-centerContent'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Entitlement')}
          </Typography>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {entitlementDisplayName}
          </Typography>
          <Box className='SodEntitlementListGroup-rowDetails'>
            <Typography kind='body2' color={CF_NEU_STL}>
              {entitlementValue}
            </Typography>
            {endpointName && endpointName.length > 0 && (
              <>
                <InterPoint color='neutralBold' font='body2' />
                <Typography kind='h2' color={CF_NEU_BLD} isNoWrap>
                  <SodApplicationGroup
                    label={endpointName}
                    firstLogo={endPointLogo ?? ''}
                  />
                </Typography>
              </>
            )}
            {listConfigurableColumns.length > 0 && listConfigurableColumns}
          </Box>
        </Box>
      );
    },
    [l]
  );

  const getTrailingContent = useCallback(
    (item: TrackedEntitlementRowType) => {
      const { riskCount, userCount, violationCount } = item;

      const isExpanded = expandedMenuId === item.entitlementValueKey;

      const menuTrigger = (
        <ButtonIcon
          size='medium'
          kind='ghost'
          ariaLabel={l('More actions')}
          isSelected={isExpanded}
          icon={<Icon kind='MoreVertical' />}
        />
      );

      const handleMenuExpand = (isMenuOpen: boolean) => {
        if (isMenuOpen) {
          setExpandedMenuId(null);
        } else {
          setExpandedMenuId(item.entitlementValueKey || null);
        }
      };

      const handleSourceChange = (
        sourceType: string,
        activeTab: string,
        listItem: TrackedEntitlementRowType
      ) => {
        navigateToDetails({
          key: String(listItem.entitlementValueKey),
          activeTab,
          isEntitlementRoute: true,
          navigation: {
            source: sourceType,
            title: listItem.entitlementDisplayName ?? '',
          },
        });
      };

      return (
        <Box className='SodEntitlementListGroup-trailingContent'>
          <Box className='SodEntitlementListGroup-rowLinkBlocks'>
            <Box className='SodEntitlementListGroup-rowLinkBlock'>
              <Typography kind='body3' color={CF_NEU_STL} isNoWrap>
                {l('Inherent Risks')}
              </Typography>
              <DSLink
                kind='button'
                onClick={() => {
                  // Store selected entitlement for details page
                  const { tracker, ...entitlementData } = item;

                  dispatch(
                    detailsActions.setSelectedEntitlement(
                      entitlementData as EntitlementDetailsRowType
                    )
                  );
                  // Set selected entitlement in Redux and navigate to reports
                  dispatch(
                    entReportsActions.setSelectedEntitlementValueKey(
                      String(item.entitlementValueKey)
                    )
                  );
                  dispatch(
                    entReportsActions.setSelectedEntitlementName(
                      item.entitlementDisplayName
                    )
                  );

                  handleSourceChange(
                    violationsEntitlement,
                    ENTITLEMENT_DETAILS_TABS.TAB_RISKS,
                    item
                  );
                }}
                text={String(riskCount)}
                size='large'
                prefixIconKind='CriticalHighRiskHexagon'
              />
            </Box>
            <Box className='SodEntitlementListGroup-rowLinkBlock'>
              <Typography kind='body3' color={CF_NEU_STL}>
                {l('Users')}
              </Typography>
              <DSLink
                kind='button'
                onClick={() => {
                  // Store selected entitlement for details page
                  const { tracker, ...entitlementData } = item;

                  dispatch(
                    detailsActions.setSelectedEntitlement(
                      entitlementData as EntitlementDetailsRowType
                    )
                  );
                  // Set selected entitlement in Redux and navigate to reports
                  dispatch(
                    entReportsActions.setSelectedEntitlementValueKey(
                      String(item.entitlementValueKey)
                    )
                  );
                  dispatch(
                    entReportsActions.setSelectedEntitlementName(
                      item.entitlementDisplayName
                    )
                  );

                  handleSourceChange(
                    violationsEntitlement,
                    ENTITLEMENT_DETAILS_TABS.TAB_USERS,
                    item
                  );
                }}
                text={String(userCount)}
                size='large'
                prefixIconKind='User'
              />
            </Box>
            <Box className='SodEntitlementListGroup-rowLinkBlock'>
              <Typography kind='body3' color={CF_NEU_STL}>
                {l('Violations')}
              </Typography>
              <DSLink
                kind='button'
                onClick={() => {
                  // Store selected entitlement for details page
                  const { tracker, ...entitlementData } = item;

                  dispatch(
                    detailsActions.setSelectedEntitlement(
                      entitlementData as EntitlementDetailsRowType
                    )
                  );

                  handleSourceChange(
                    violationsEntitlement,
                    ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
                    item
                  );
                }}
                text={String(violationCount)}
                size='large'
                prefixIconKind='SOD'
              />
            </Box>
          </Box>
          {/* Placeholder Button */}
          {!isSidePanelVisible && (
            <Box className='SodEntitlementListGroup-rowKabobWrapper'>
              <Menu
                options={initialEntOptionsWithIcons}
                onChange={(e: EntKebabOptions) => {
                  if (e.value === 'report') {
                    // Set selected entitlement in Redux and navigate to reports
                    dispatch(
                      entReportsActions.setSelectedEntitlementValueKey(
                        String(item.entitlementValueKey)
                      )
                    );
                    dispatch(
                      entReportsActions.setSelectedEntitlementName(
                        item.entitlementDisplayName
                      )
                    );
                    navigateToUrl(AAG_SOD_ENTITLEMENT_REPORT);
                  } else if (e.icon === 'ArrowRight') {
                    // Store selected entitlement for details page
                    const { tracker, ...entitlementData } = item;

                    dispatch(
                      detailsActions.setSelectedEntitlement(
                        entitlementData as EntitlementDetailsRowType
                      )
                    );
                    // Set selected entitlement in Redux and navigate to reports
                    dispatch(
                      entReportsActions.setSelectedEntitlementValueKey(
                        String(item.entitlementValueKey)
                      )
                    );
                    dispatch(
                      entReportsActions.setSelectedEntitlementName(
                        item.entitlementDisplayName
                      )
                    );

                    handleSourceChange(
                      violationsEntitlement,
                      ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY,
                      item
                    );
                  }
                }}
                minOptionsForSearch={10}
                trigger={menuTrigger}
                setIsExpanded={(isMenuOpen: boolean) => {
                  handleMenuExpand(isMenuOpen);
                }}
                placement='bottom-end'
                isPortalEnabled
              />
            </Box>
          )}
        </Box>
      );
    },
    [
      expandedMenuId,
      isSidePanelVisible,
      l,
      navigateToDetails,
      dispatch,
      initialEntOptionsWithIcons,
      navigateToUrl,
    ]
  );

  // Clear filter on no data
  const handleClearBtn = useCallback(() => {
    dispatch(entitlementActions.setActiveFilters([]));
  }, [dispatch]);

  // Render body here
  const renderListBody = useCallback(() => {
    // No data Screens
    if (isZeroCount) {
      return (
        <SodMessage
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          handleFilterClearBtn={handleClearBtn}
          defaultTitle={l('No Entitlements')}
          defaultMessage={l('Keep up the good work!')}
        />
      );
    }

    return (
      <ListRowGroup className='Sod-ViolationsTabList--isListRowGroup'>
        {/* List rows */}
        {listRows.map((row) => {
          const {
            tracker: { id, isRowDetailsOpen, isTransition, isFirstAddded },
          } = row;

          const rowClasses = cx(
            'Sod-ViolationsTabList-listRow',
            isRowDetailsOpen && 'Sod-ViolationsTabList-listRow--isConnected',
            isTransition && 'Sod-ViolationsTabList-listRow--isTransition',
            isFirstAddded && 'Sod-ViolationsTabList-listRow--isFirstAddded'
          );

          const selectedColumns = activeColumns.filter(
            (column) => column.isChecked
          );

          const listConfigurableColumns = selectedColumns
            .filter((column) => column.isChecked)
            .map((column, index) => {
              const { label, value } = column;
              let columnValue = null;

              // Handle nested riskObjects data
              if (
                value === 'riskPriority' ||
                value === 'riskType' ||
                value === 'riskName'
              ) {
                columnValue = row.riskObjects[0]?.[value] ?? null;
              } else {
                columnValue = row[value as keyof typeof row] ?? null;
              }

              return (
                <React.Fragment key={value}>
                  {index === 0 && (
                    <InterPoint color='neutralBold' font='body2' />
                  )}
                  <SodColumnChip
                    name={label}
                    value={String(columnValue)}
                    isPrefixInterpoint={index < selectedColumns.length - 1}
                  />
                </React.Fragment>
              );
            });

          return (
            <ListRow
              key={id}
              className={rowClasses}
              dataTestId={testIds.SOD_ENTITLEMENT_TAB}
              isCheckboxDisabled
              leadingComp={
                <Icon kind='Entitlement' size='medium' color={CF_SEC_MED} />
              }
              centerContent={
                <Box className='SodEntitlementListGroup-centerContent'>
                  {getCenterContent(row, listConfigurableColumns)}
                </Box>
              }
              trailingContent={!isSidePanelVisible && getTrailingContent(row)}
              handleRowClick={() => {
                // Store selected entitlement data in Redux store for details page navigation
                // Exclude tracker object and pass clean entitlement data
                const { tracker, ...entitlementData } = row;

                dispatch(
                  detailsActions.setSelectedEntitlement(
                    entitlementData as EntitlementDetailsRowType
                  )
                );
                handleRowClick(id);
              }}
              isRowDetailsOpen={isRowDetailsOpen}
              connectorDistance='1.125rem'
              isRowConnectTransitionEnabled
            />
          );
        })}
        {/* Observer Element */}
        {!isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isListError && isValidApiCall && <SodListLoader count={3} />}
        {/* Retry mechanism */}
        {isListError && (
          <SodReloader triggerRefresh={() => setIsListError(false)} />
        )}
      </ListRowGroup>
    );
  }, [
    listObserverElement,
    isSidePanelVisible,
    listRows,
    isValidApiCall,
    isListError,
    handleRowClick,
    getCenterContent,
    getTrailingContent,
    l,
    isZeroCount,
    isSearchActive,
    isFilterActive,
    handleClearBtn,
    activeColumns,
    dispatch,
  ]);

  return (
    <Box
      className='Sod-ViolationsEntitlementTabList'
      dataTestId={testIds.SOD_VIOLATIONS_TAB_LIST}>
      {renderListBody()}
    </Box>
  );
}

export default memo(EntitlementTabList);
